
import {Component, Vue, Watch} from 'vue-property-decorator';
import {colors, Dialog, Platform} from 'quasar';
import {
	AnalyticsService,
	BRAND,
	BRAND_CONFIG_COLORS,
	BRAND_CONFIG_IMAGES,
	BRAND_CONFIG_IMAGES_BASE64,
	CapGoStore,
	ContainerTransitionMappingStore,
	CustomerProfileStore,
	DeepLinkStore,
	DependencyService,
	DisplayStore,
	ERouteName,
	EventsService,
	FeatureFlag,
	InjectCustomHTML,
	LocationHoursStore,
	LocationService,
	LocationStore,
	OrderStore,
	PERMISSION_STATE,
	PromiseDateStore,
	PushService,
	SessionStore,
	SimpleMenuStore,
	SpecialsStore,
	TicketsStore,
	TrackingService,
	UIStore,
	WebService,
} from '@customer-portal/shared';
import router from '@/router';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import {useFavicon, useTitle} from '@vueuse/core';
import PermissionsWrapper from './components/PermissionsWrapper.vue';
import type {colorsRgba} from 'quasar/dist/types/utils/colors';
import {SplashScreen} from '@cap/@capacitor/splash-screen';
import {App as CapacitorApp} from '@cap/@capacitor/app';
import {NetworkConnectivityBanner} from '@customer-portal/shared/src/components';
import type {
	IAuthConfig,
	IBrandConfigVariables,
	ILocation,
	TBrandConfigImageBase64,
} from '@customer-portal/shared';
import BinaryUpdateDialog from '@customer-portal/shared/src/components/BinaryUpdateDialog.vue';
import {CustomHTMLService} from '@customer-portal/shared/src/services/customHTML.service';

interface ICSSProperties extends IBrandConfigVariables {
	subtitleColor: string;
	titleColor: string;
}


@Component({
	components: {
		LoadingOverlay,
		NetworkConnectivityBanner,
		PermissionsWrapper
	}
})
export default class App extends Vue {
	// Represents completion of all web service requests needed for app launch
	internalAppReady = false;

	// Indicates a successful completion of the requests needed to bootstrap the app.
	// Allows us to skip re-making the requests as auth config changes.
	bootstrapRequestsSucceeded = false;

	showLoadingOverlay = true;
	cssProperties: ICSSProperties = {
		// colors from config service
		actionPrimaryColor: '#CC2940', // action
		backgroundColor: '#F2EEE3',
		backgroundSecondaryColor: '#F44D21', // #6
		bootScreenColor: '#8A2432',
		fullscreenBackgroundColor: '#F44D21', // #7
		giftCardColor: '#211F20',
		memberCardColor: '#8A2432',
		navColor: '#8A2432',
		pointsTextColor: '#6A5357',
		qrPageColor: '#211F20',
		rewardClaimedScreenColor: '#8a2432',
		singleHighlightColor: '#F44D21', // #4
		loyaltyLoginButtonBackgroundColor: '#FCBA34',
		loyaltyLoginButtonTextColor: '#0F3B35',
		headerBackgroundColor: '#0F3B35',
		headerTextColor: '#FFFFFF',
		subHeaderBackgroundColor: '#E7DCC4',
		subHeaderTextColor: '#0F3B35',
		serviceTypeIconColor: '#25292F',
		borderGradientColor1: '#4CD6C2',
		borderGradientColor2: '#25292F',

		mapPageLocationSelectorButtonTextColor: '#25292F',
		mapPageLocationSelectorButtonBackgroundColor: '#FFFFFF',
		mapPageLocationSelectorButtonBorderColor: '#25292F',
		mapPagePickupMethodSelectorButtonTextColor: '#FFFFFF',
		mapPagePickupMethodSelectorButtonBackgroundColor: '#25292F',
		mapPageStartOrderButtonBackgroundColor: '#4CD6C2',
		mapPageStartOrderButtonTextColor: '#FFFFFF',
		mapPageSelectedLocationBorderColor: '#4CD6C2',
		mapPageDatePickerColor: '#25292F',

		orderPageCartButtonBackgroundColor: '#25292F',
		orderPageCartButtonIconColor: '#FFFFFF',
		orderPagePointsStatusBarBackgroundColor: '#FFFFFF',
		orderPagePointsStatusBarBarColor: '#F2D698',
		orderPagePointsStatusBarLeftTextColor: '#25292F',
		orderPagePointsStatusBarRightTextColor: '#C3C4C6',
		orderPageHeaderLargeTextColor: '#FFFFFF',
		orderPageHeaderSmallTextColor: '#FFFFFF80',
		orderPageActiveNavColor: '#FCBA34',
		orderPageRecentsBackgroundColor: '#F2BC52',
		orderPageRecentsTextColor: '#25292F',
		orderPageRecentsAmountTextColor: '#242424',
		orderPageLoyaltyInfoTextColor: '#ffffff80',

		customerLoginBackgroundColor: '#FFFFFF',
		customerLoginWelcomeTextColor: '#25292F',
		customerLoginButtonBackgroundColor: '#4CD6C2',
		customerLoginButtonTextColor: '#25292F',
		customerGuestOrderButtonBackgroundColor: '#FFFFFF',
		customerGuestOrderButtonTextColor: '#25292F',
		appFont: 'Arial',
		largeTextFont: 'Arial',

		giftCardBackgroundImage: 'none',
		loginBackgroundImage: 'none',
		loginBackgroundImageDesktop: 'none',
		loyaltyInfoBackgroundImage: 'none',
		loyaltySignupRewardImage: 'none',
		memberCardBackgroundImage: 'none',
		qrPageBackgroundImage: 'none',

		titleColor: '#000000',
		subtitleColor: 'var(--color-gray)',
	};

	private promiseDateRevalidateIntervalSeconds = 60;
	private promiseDateRevalidateInterval: number | null = null;

	get brandConfigAsCssProperties(): Record<string, string> {
		const variables: Record<string, string> = {};
		const brandConfigKeys = Object.keys(this.cssProperties) as Array<keyof IBrandConfigVariables>;

		// Initialize variables with the default values.
		brandConfigKeys.forEach(brandConfigKey => {
			const appInitialValue = this.cssProperties[brandConfigKey];

			variables[`--${brandConfigKey}`] = appInitialValue;

			// generate additional colors
			if(appInitialValue.startsWith('#')) {
				variables[`--${brandConfigKey}RGB`] = this.rgbaObjectToRGBString(colors.hexToRgb(appInitialValue));
				variables[`--${brandConfigKey}Light`] = colors.lighten(appInitialValue, 50);
				variables[`--${brandConfigKey}LightRGB`] = this.rgbaObjectToRGBString(colors.hexToRgb(variables[`--${brandConfigKey}Light`]));
			}
		});

		return variables;
	}

	get authConfig() {
		return SessionStore.authConfig;
	}

	get brandConfig() {
		return SessionStore.brandConfig;
	}

	get selectedBackground() {
		return SessionStore.selectedBackground;
	}

	get customerProfile() {
		return CustomerProfileStore.customerProfile;
	}

	get brandName() {
		return SessionStore.brandConfig?.brandName || '';
	}

	get appReady() {
		return this.internalAppReady;
	}

	get location() {
		return OrderStore.location;
	}

	// These were all originally part of WebService.startLoggedIn. With anon browsing, we need to make these
	// requests during app creation since many components rely on the data being present. If we want to lessen
	// initial load, we can push these requests to the components themselves, though we may want to introduce
	// a cached request mechanic like in nWA.
	appBootStrapRequests() {
		const requests = [
			SimpleMenuStore.fetchMenu(),
			SpecialsStore.fetchSpecials(),
			ContainerTransitionMappingStore.fetchContainerTransitionMappings(),
		];

		// Locations request will be made during WebService.startLoggedIn so we can get favorite data.
		// Only attach the locations promise if we are unauthenticated when attempting our bootstrap requests
		// so we don't double dip on initial load of app when authConfig is present.
		if(!SessionStore.authConfig) {
			requests.push(LocationStore.fetchLocations());
		}

		return requests;
	}

	// Instead of using :style on the q-app element we set these
	// on the document root element so that we can use them in
	// modals as well
	// TODO: this probably should happen only once. Consider an unwatch when upgrading to comp API.
	//       https://stackoverflow.com/a/65933627/356016
	@Watch('brandConfigAsCssProperties', {deep: true})
	onCssPropertiesChange() {
		for(const key in this.brandConfigAsCssProperties) {
			document.documentElement.style.setProperty(key, this.brandConfigAsCssProperties[key]);
		}

		this.$nextTick(() => {
			this.updateThemeColor();
		});
	}

	@Watch('customerProfile', {immediate: true})
	async onCustomerProfileChange() {
		if(this.customerProfile && this.authConfig !== null) {
			await WebService.loggedInStarted;
			if(!this.customerProfile.acceptedTOS && this.$route.name !== ERouteName.AcceptTOS) {
				await this.$router.push({
					name: ERouteName.AcceptTOS
				});
			}
		}
	}

	@Watch('authConfig')
	async onAuthChange() {
		if(!WebService.started) {
			try {
				await WebService.start();
			} catch(error) {
				console.error(error);
				// If we couldn't start then it might mean that this is first launch and something
				// is not in prod yet (default env). If there is no stored authConfig then let's redirect
				// to login (which happens below)
			}
		}

		if(!this.bootstrapRequestsSucceeded) {
			try {
				await Promise.all(this.appBootStrapRequests());
				this.bootstrapRequestsSucceeded = true;
			} catch(error) {
				console.log('bootstrap error', error);
				// CPH - TODO: What should happen if the bootstrap requests fail?
				this.showLoadingOverlay = false;
				return;
			}
		}

		if(this.authConfig === null) { // logout
			EventsService.reset();
			AnalyticsService.logout();
			this.showLoadingOverlay = true;

			if(this.$router.currentRoute.name !== ERouteName.Login) {
				/*
				 * Almost certainly, we're arriving here through the
				 * Launch page and a process that should've initialized
				 * SessionStore.initialPath. If initialPath matches a
				 * route that can be accessed anonymously, redirect to
				 * that route. Otherwise, reroute to Login.
				 *
				 *
				 * With the addition of guest browsing, we have a chance to get redirected
				 * via a nav guard (eg not logged in and navigating directly to /order).
				 * An error is thrown if we navigate in a nav guard during App created.
				 * In App created, we call onAuthChange directly.
				 */
				this.$nextTick(async () => {
					await SessionStore.goToInitialRoute();
					await SplashScreen.hide();
				});
			}
			this.showLoadingOverlay = false;
		} else { // login
			EventsService.setUser(this.authConfig.userBase);
			this.showLoadingOverlay = true;
			await WebService.startLoggedIn(this.authConfig);
			// Wait to redirect until after the user has logged in
			if(this.customerProfile) {
				if(Platform.is.capacitor && this.customerProfile && this.customerProfile.pushNotificationsEnabled) {
					PushService.startupRegister();
				}
				AnalyticsService.login();
				WebService.handleScreenPops();
			}

			try {
				await SessionStore.fetchBrandConfig();
				this.showLoadingOverlay = false;
			} catch(e) {
				console.error('Failed to fetch brand config.', e);
				WebService.showError(e);
			}
		}

		// Fix background if it's not selectable
		if(this.brandConfig) {
			// If none is set, or it's out of bounds, then reset to 0
			const backgroundImageMax = this.brandConfig.backgroundImages.length - 1;
			if(!this.selectedBackground || this.selectedBackground > backgroundImageMax) {
				SessionStore.setSelectedBackground(0);
			}
		}

		// Clean up Vuex store
		if(!SpecialsStore.currentSpecialIndex) {
			SpecialsStore.setCurrentSpecialIndex(0);
		}

		await this.initStores(); // Init order/ticket data

		// Just in case we hide one more time
		await SplashScreen.hide();
	}

	@Watch('brandConfig', {immediate: true, deep: true})
	async onBrandConfigChange() {
		if(this.brandConfig) {
			BRAND_CONFIG_COLORS.forEach(configName => {
				// Only set color if it exists in brandConfig,
				// this will fall back to the defaults set in cssProperties above if not set
				const color = this.brandConfig?.[configName];

				if(color) {
					Vue.set(this.cssProperties, configName, color);
				}
			});

			for(const configName of BRAND_CONFIG_IMAGES) {
				// Fallback to initial value if it's not set
				// This should give us backwards compatibility while
				// we wait for the server to start providing it
				if(this.brandConfig?.[configName]) {
					let image = `url(${this.brandConfig[configName]})`;

					// Fix for flickering images
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
					if(BRAND_CONFIG_IMAGES_BASE64.includes(configName as TBrandConfigImageBase64)) {
						image = `url(${await WebService.base64Image(this.brandConfig[configName])})`;
					}

					if(this.cssProperties[configName] !== image) {
						Vue.set(this.cssProperties, configName, image);
					}
				} else {
					Vue.set(this.cssProperties, configName, 'none');
				}
			}

			const favicon = useFavicon();
			if(favicon) {
				favicon.value = this.brandConfig.logoImage;
			}

			// Update browser tab title with brand name
			const title = useTitle();
			if(title && this.brandConfig.brandName) {
				title.value = this.brandConfig.brandName;
			}

			// Fonts
			this.cssProperties.appFont = this.brandConfig?.appFont ?? 'Arial';
			this.cssProperties.largeTextFont = this.brandConfig?.largeTextFont ?? 'Arial';

			// Brand config might have returned after the customHTML global route guard has executed, so manually trigger
			// an injection just in case. If the html has already been injected for the route, the service won't inject it again
			CustomHTMLService.handleNavigationInjection(router.currentRoute);

			this.$nextTick(() => {
				this.updateThemeColor();
			});
		}
	}

	@Watch('location', {immediate: true})
	async updatePromiseDateStoreLocation(value: ILocation) {
		if(value === null) {
			PromiseDateStore.setLocationDependencies({
				locationTimezone: null,
				locationHours: null,
				currentWaitTimeSeconds: null
			});
		} else {
			if(!WebService.started) { // Need to wait for this to resolve before attempting to get location hours
				await WebService.baseStarted;
			}

			await LocationHoursStore.fetchLocationHours(value.id);
			const hours = LocationHoursStore.locationHours(value.id);

			PromiseDateStore.setLocationDependencies({
				locationTimezone: value.timezoneId,
				locationHours: hours,
				currentWaitTimeSeconds: value.currentWaitTime * 60
			});
		}
		PromiseDateStore.revalidatePromiseDate();
	}

	async sleep(milliseconds: number) {
		return new Promise(resolve => {
			return setTimeout(resolve, milliseconds);
		});
	}

	// We check for the auth GET param as well as for a tempAuthConfig.
	// If we see the auth GET param, we use that and put it into tempAuthConfig so that
	// if the user refreshes the page, we can use the auth from tempAuthConfig.
	// We try up to 10 times for the auth to work and then we give up.
	async checkForAuth() {
		let authChanged = false;
		// We have to do this "native" because Vue doesn't have this all wired up this early
		let auth: IAuthConfig | null = null;
		let search: string = window.location.search;
		// This is for when you are running the dev server locally which uses
		// the hash for the path. We need to check the hash for the auth param
		if(process.env.NODE_ENV === 'development' && location.hash !== '') {
			if(location.hash.indexOf('?') !== -1) {
				search = location.hash.substring(location.hash.indexOf('?'));
			}
		}
		const params = new URLSearchParams(search);
		if(params.has('auth')) {
			const authParam = params.get('auth');
			if(authParam) {
				auth = JSON.parse(atob(authParam)) as IAuthConfig;
				SessionStore.setTempAuthConfig(auth);
			}
		} else if(SessionStore.tempAuthConfig !== null) {
			auth = SessionStore.tempAuthConfig;
		}

		if(auth && 'userBase' in auth && 'apiKey' in auth && 'franchisorBase' in auth) {
			// If we got auth at all assume it changed
			authChanged = true;
			let authSuccessful = false;
			for(let i = 0; i < 10; i++) {
				if(await WebService.testAuthConfig(auth)) {
					authSuccessful = true;
				} else {
					// Wait 1 second between tries
					await this.sleep(1000);
				}
				if(authSuccessful) {
					break;
				}
			}
			// Always clear
			SessionStore.clearTempAuthConfig();
			if(authSuccessful) {
				SessionStore.setAuthConfig(auth);
			} else {
				// Assume it didn't change if we couldn't validate it
				authChanged = false;
			}
		}


		return authChanged;
	}

	/**
	 * Spot to add init calls to stores that need to run some code at startup
	 *
	 * Calls should be non-blocking (no await) unless strictly necessary.
	 */
	async initStores() {
		return Promise.all([
			OrderStore.init(),
			TicketsStore.init(),
		]);
	}

	async created() {
		SessionStore.setBrand(BRAND.GENERIC);
		SessionStore.setInitialPath(null); // Clear out old initial paths
		DependencyService.setRouter(router);
		EventsService.registerAppInfo(await WebService.getDomain(), WebService.getAppVersion());

		// Do this before we start doing any routing so we don't have a visible switch from the default of false
		// to true when on desktop. I think we didn't notice this before because the only desktop page was order,
		// which had to wait on a bunch of other things to complete before loading.
		DisplayStore.init();

		// This init method informs the capacitor updater plugin that we have successfully loaded up our webapp
		try {
			await CapGoStore.init();
		} catch(error) {
			console.error('CapGoStore initialization failed', error);
		}

		// Go ahead and attach the global router
		// Brand Config might not be available (no data in SessionStore) and the request hasn't returned
		// If that's the case, when the service the guard uses checks to see what to inject, it will get an
		// empty array and not inject anything
		router.beforeEach(InjectCustomHTML);

		// We need to check this first so we can grab the auth from the URL before we redirect
		const authCheck = this.checkForAuth();
		if(this.$route.name !== 'Launch') {
			const path = WebService.getCurrentPath();

			if(path.includes('mobile/tokenize')) {
				// This needs precedence over anything else
				SessionStore.setInitialPath(path);
			} else if((path === '/' || path === '/home' || path === '/home/') && DisplayStore.isDesktop) {
				// If the path sends us to Home, we want to use OrderLocations for the new CPDesktop.
				if(OrderStore.selectedLocationId) {
					// route to last selected location's modal
					SessionStore.setInitialLocation({
						name: ERouteName.Order,
						params: {
							showLocation: 'yes',
						},
					});
				} else {
					SessionStore.setInitialLocation({name: ERouteName.OrderLocations});
				}
			} else {
				SessionStore.setInitialPath(path);
			}

			try {
				await this.$router.push({
					name: ERouteName.Launch,
					params: {
						bounceToHome: 'false' // String booleans are gross but it's all that's allowed
					}
				});
			} catch(error) {
				console.error('Error pushing route in App.vue', error);
			}
		}
		const authChanged = await authCheck;
		await AnalyticsService.init();

		CapacitorApp.removeAllListeners();
		if(Platform.is.capacitor) {
			CapacitorApp.addListener('appUrlOpen', (data) => {
				if(this.customerProfile) {
					console.log('appUrlOpen', data);
					if(data.url.indexOf('tp') === 0) {
						// This is a deep link
						DeepLinkStore.handleDeepLink(data.url);
					} else {
						WebService.processOpenUrl(data.url);
					}
				}
			});
			// Here is where we will handle our redirects, you can only have 1
			const launchUrl = await CapacitorApp.getLaunchUrl();
			if(launchUrl && launchUrl.url && launchUrl.url !== '') {
				if(launchUrl.url.indexOf('tp') === 0) {
					// This is a deep link
					DeepLinkStore.handleDeepLink(launchUrl.url);
				} else {
					SessionStore.setInitialPath(launchUrl.url);
				}
			}
		}

		CapacitorApp.addListener('appStateChange', async (state) => {
			// N.B.: This fires on a tab change or when the browser regains focus (on desktop)
			if(state.isActive) {
				console.log('resuming app');
				TrackingService.trackEvent(TrackingService.EVENT_TYPE_APP_OPEN).then();
				if(WebService.started) {
					await WebService.reload();
				}
				// Init stores again, this does stuff like refresh the ticket
				this.initStores().catch((err) => {
					console.error(err);
				});
				// Recheck push ability
				if(Platform.is.capacitor && this.customerProfile && this.customerProfile.pushNotificationsEnabled) {
					await PushService.startupRegister();
				}
				if(Platform.is.capacitor && SessionStore.isEnabled(FeatureFlag.PUSH_NOTIFICATIONS) && this.customerProfile && UIStore.showPushNotificationDisabled) {
					// Check to see if they fixed it in settings and then came back
					if(await PushService.requestPermission()) {
						UIStore.setShowPushNotificationDisabled(false);
					}
				}
				if(Platform.is.capacitor) {
					const locationState = await LocationService.getPermissionState(); // Force vuex update
					if(locationState === PERMISSION_STATE.GRANTED) {
						// If we are granted then turn off both these if they are open
						UIStore.setShowGeolocationRequest(false);
						UIStore.setShowGeolocationDisabled(false);
					} else if(locationState === PERMISSION_STATE.DENIED && UIStore.showGeolocationRequest) {
						// If we are denied and we were showing the request then let's show disabled
						UIStore.setShowGeolocationRequest(false);
						UIStore.setShowGeolocationDisabled(true);
					}

					try {
						// we also want to check for bundle updates each time the app becomes active
						const result = await CapGoStore.checkForUpdatesAndDownload();

						if(result === false) {
							// A binary update is required
							Dialog.create({
								component: BinaryUpdateDialog,
								persistent: true,
							});
						}
					} catch(error) {
						console.error('CapGoStore update check failed', error);
					}
				}
			} else {
				TrackingService.trackEvent(TrackingService.EVENT_TYPE_APP_CLOSE).then();

				// if we have a bundle update downloaded and ready to go, we set it live as we close the app
				// next time the user focuses the app, it will load up with the update.
				if(CapGoStore.hasPendingUpdate) {
					await CapGoStore.installPendingUpdate();
				}
			}
		});

		// We are doing this here because we first want to check for the auth GET param
		if(!authChanged) {
			await this.onAuthChange();
		}


		this.internalAppReady = true;

		this.$nextTick(() => {
			this.updateThemeColor();
		});

		PromiseDateStore.revalidatePromiseDate();
		this.promiseDateRevalidateInterval = window.setInterval(
			() => {
				PromiseDateStore.revalidatePromiseDate();
			},
			this.promiseDateRevalidateIntervalSeconds * 1000
		);

		try {
			// This calls out to the server to see if we have an available update and downloads it.
			// If we're running on web (not in a mobile app context), this will noop.

			// We call this last because it may trigger a dialog, and we want that to happen after the splash screen is hidden.
			const result = await CapGoStore.checkForUpdatesAndDownload();

			if(result === false) {
				// A binary update is required
				Dialog.create({
					component: BinaryUpdateDialog,
					persistent: true,
				});
			}
		} catch(error) {
			console.error('CapGoStore update check failed', error);
		}
	}

	destroyed() {
		CapacitorApp.removeAllListeners();
		EventsService.unregisterAppInfo();

		if(this.promiseDateRevalidateInterval !== null) {
			window.clearInterval(this.promiseDateRevalidateInterval);
		}
	}

	rgbaObjectToRGBString(colorObject: colorsRgba): string {
		return `${colorObject.r},${colorObject.g},${colorObject.b}`;
	}

	updateThemeColor() {
		const backgroundColor = getComputedStyle(document.documentElement)
			.getPropertyValue('--backgroundColor')
			.trim();

		if(backgroundColor) {
			let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;

			if(!themeColorMeta) {
				themeColorMeta = document.createElement('meta');
				themeColorMeta.name = 'theme-color';
				document.head.appendChild(themeColorMeta);
			}

			themeColorMeta.content = backgroundColor;
		}
	}
}
